import api from './api';
import { Appointment } from '../types/appointment.types';

export interface BarberStats {
    todayAppointments: number;
    completedToday: number;
    pendingToday: number;
    totalCompleted: number;
    pendingPayments: number;
    barberName: string;
    branchName: string;
    pricePerService: number;
}

export interface PaymentRequest {
    appointmentId?: number;
    amount: number;
    paymentMethod: 'BANK_TRANSFER' | 'CASH' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'MERCADO_PAGO';
    transactionReference?: string;
    receiptImageUrl?: string;
}

export interface PaymentResponse {
    id: number;
    appointmentId: number;
    clientName: string;
    amount: number;
    paymentMethod: string;
    status: 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'REFUNDED';
    transactionReference?: string;
    paidAt: string;
    createdAt: string;
    receiptUrl?: string;
    appointmentDate?: string;
    serviceDescription?: string;
}

export interface UploadResponse {
    url: string;
    filename: string;
}

export const barberDashboardService = {
    getStats: async (): Promise<BarberStats> => {
        const response = await api.get<BarberStats>('/barber/dashboard/stats');
        return response.data;
    },

    getTodayAppointments: async (): Promise<Appointment[]> => {
        const response = await api.get<Appointment[]>('/barber/dashboard/appointments/today');
        return response.data;
    },

    getAppointmentsByDate: async (date: string): Promise<Appointment[]> => {
        const response = await api.get<Appointment[]>('/barber/dashboard/appointments', {
            params: { date }
        });
        return response.data;
    },

    getAllAppointments: async (): Promise<Appointment[]> => {
        const response = await api.get<Appointment[]>('/barber/dashboard/appointments/all');
        return response.data;
    },

    markAsInProgress: async (appointmentId: number): Promise<Appointment> => {
        const response = await api.post<Appointment>(`/barber/dashboard/appointments/${appointmentId}/in-progress`);
        return response.data;
    },

    markAsCompleted: async (appointmentId: number): Promise<Appointment> => {
        const response = await api.post<Appointment>(`/barber/dashboard/appointments/${appointmentId}/complete`);
        return response.data;
    },

    registerPayment: async (request: PaymentRequest): Promise<PaymentResponse> => {
        const response = await api.post<PaymentResponse>('/barber/dashboard/payments', request);
        return response.data;
    },

    // Complete appointment without payment - creates pending payment
    completeWithPendingPayment: async (appointmentId: number): Promise<PaymentResponse> => {
        const response = await api.post<PaymentResponse>(`/barber/dashboard/appointments/${appointmentId}/complete-pending`);
        return response.data;
    },

    // Get all pending payments
    getPendingPayments: async (): Promise<PaymentResponse[]> => {
        const response = await api.get<PaymentResponse[]>('/barber/dashboard/payments/pending');
        return response.data;
    },

    // Complete a pending payment
    completePendingPayment: async (paymentId: number, request: PaymentRequest): Promise<PaymentResponse> => {
        const response = await api.post<PaymentResponse>(`/barber/dashboard/payments/${paymentId}/complete`, request);
        return response.data;
    },

    // Update an existing payment
    updatePayment: async (paymentId: number, request: PaymentRequest): Promise<PaymentResponse> => {
        const response = await api.put<PaymentResponse>(`/barber/dashboard/payments/${paymentId}`, request);
        return response.data;
    },

    // Upload receipt image
    uploadReceiptImage: async (file: File): Promise<UploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post<UploadResponse>('/uploads/receipt', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Cancel appointment with reason (barber initiated)
    cancelAppointment: async (appointmentId: number, reason: string): Promise<Appointment> => {
        const response = await api.post<Appointment>(`/barber/dashboard/appointments/${appointmentId}/cancel`, {
            reason
        });
        return response.data;
    },

    // Reschedule appointment to new date/time (barber initiated)
    rescheduleAppointment: async (appointmentId: number, newDate: string, newTime: string, reason: string): Promise<Appointment> => {
        const response = await api.post<Appointment>(`/barber/dashboard/appointments/${appointmentId}/reschedule`, {
            newDate,
            newTime,
            reason
        });
        return response.data;
    },
};
